import { CANONICAL_REGIME_LABELS, type CanonicalRegime } from '../fiscal/canonicalRegime';
import type {
	PlanRecommendationResponse,
	PlanRecommendationSourceKey,
} from '../services/planRecommendation.service';
import { PLAN_CATALOG, planSlugForFamily, type PlanFamily } from './catalog';

/**
 * EL plan recomendado de toda la web, como lo ve el front: qué plan mensual le
 * corresponde a un usuario y si un plan cubre sus regímenes. La REGLA no vive
 * aquí: la resuelve heru-user-management-engine (módulo planrecommendation) para
 * la web y para las campañas, así que el WhatsApp de un journey y la app nunca
 * recomiendan planes distintos. Aquí solo se traduce la respuesta y se arma el
 * copy. Lo usan /planes, el inicio, los hitos, el onboarding y el aviso del
 * carrito; ninguna superficie decide el plan por su cuenta.
 */

export type PlanRegimeSource = PlanRecommendationSourceKey;

export type PlanRegimeFit = {
	/** Regímenes con declaración mensual que le conocemos (sin sueldos): los que un plan tiene que cubrir. */
	regimes: CanonicalRegime[];
	/** Todo lo que le conocemos, sueldos incluido: lo que le decimos al explicar la recomendación. */
	knownRegimes: CanonicalRegime[];
	source: PlanRegimeSource | null;
	/** El plan que le corresponde; null sin dato. */
	recommended: PlanFamily | null;
	/** ¿El plan cubre sus regímenes? Sin dato, cualquiera. */
	covers: (family: PlanFamily) => boolean;
};

export const NO_FIT: PlanRegimeFit = {
	regimes: [],
	knownRegimes: [],
	source: null,
	recommended: null,
	covers: () => true,
};

/** La respuesta del endpoint en la forma que consumen las superficies. */
export const toPlanRegimeFit = (response: PlanRecommendationResponse | null | undefined): PlanRegimeFit =>
	response
		? {
				regimes: response.regimes ?? [],
				knownRegimes: response.known_regimes ?? [],
				source: response.source,
				recommended: response.plan_family,
				covers: (family) => response.coverage?.[family] ?? true,
			}
		: NO_FIT;

/** "Actividad empresarial y RESICO" — los regímenes que explican la recomendación. */
export const regimesLabel = (regimes: readonly CanonicalRegime[]): string => {
	const names = regimes.map((regime) => CANONICAL_REGIME_LABELS[regime]);
	if (names.length <= 1) return names[0] ?? '';
	return `${names.slice(0, -1).join(', ')} y ${names[names.length - 1]}`;
};

export const planDisplayName = (family: PlanFamily): string =>
	PLAN_CATALOG[planSlugForFamily(family)].displayName;

/** Por qué un plan no es el recomendable, en el copy que ve el usuario. */
export const planMismatchMessage = (fit: PlanRegimeFit): string => {
	const regimes = `Tu régimen es ${regimesLabel(fit.regimes)} y este plan no lo cubre`;
	return fit.recommended
		? `${regimes}. El que te corresponde es ${planDisplayName(fit.recommended)}.`
		: `${regimes}.`;
};
